import { Link } from 'react-router-dom';
import { EmptyState, MetricCard, Section } from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { OrdersResponse } from '@/lib/types.js';
import { SectionPager, SectionTitle, type SectionPaging } from './shared.js';

/**
 * Promotion code 使用记录 — separate from redeem-code activations (those
 * are full plan unlocks; promos just discount a Stripe checkout).
 */
export function PromotionsSection({
  data,
  offset,
  onOffsetChange,
  pageSize,
}: { data: OrdersResponse['promotions'] } & SectionPaging): JSX.Element {
  const { fmtDateTime } = useFmt();
  return (
    <section>
      <SectionTitle
        icon="🎁"
        title="Promotion code 使用"
        emptyHint={data.total === 0 ? '暂无人使用' : undefined}
      />

      {data.by_code.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {data.by_code.slice(0, 4).map((b) => (
            <MetricCard
              key={b.code}
              label={b.code}
              value={b.count}
              unit="次"
              tone="info"
              hint={`累计抵扣 ¥${(b.total_discount_cents / 100).toFixed(2)}`}
            />
          ))}
        </div>
      )}

      <Section size="sm" muted={data.rows.length === 0}>
        {data.rows.length === 0 ? (
          <EmptyState
            title="还没有人用过 promotion code"
            hint="去「折扣码」页创建一个"
            icon="🎟️"
            compact
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left py-2 px-2">用户</th>
                  <th className="text-left py-2 px-2">码</th>
                  <th className="text-left py-2 px-2">折扣</th>
                  <th className="text-left py-2 px-2">抵扣金额</th>
                  <th className="text-left py-2 px-2">使用时间</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/[0.08]">
                    <td className="px-2 py-2">
                      <Link to={`/users/${r.user_id}`} className="text-shark-300 hover:text-shark-200">
                        {r.user_email ?? r.user_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-2 py-2 font-mono text-slate-200">{r.promo_code}</td>
                    <td className="px-2 py-2 text-shark-300">{r.discount_percent}% off</td>
                    <td className="px-2 py-2 text-slate-300">
                      {r.discount_cents !== null
                        ? `¥${(r.discount_cents / 100).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-slate-400 text-xs">
                      {fmtDateTime(r.redeemed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <SectionPager total={data.total} paging={{ offset, onOffsetChange, pageSize }} />
    </section>
  );
}
